import React from "react";
import { Link } from "react-router-dom";
import {
  Heart,
  Users,
  TrendingUp,
  Award,
  Clock,
  MapPin,
  Briefcase,
  ArrowRight,
  Phone,
} from "lucide-react";
import careersHeroImage from "../assets/careers-hero.jpg";

const Careers = () => {
  const breadcrumbs = [
    { label: "Home", link: "/" },
    { label: "Careers", link: null },
  ];

  const benefits = [
    {
      icon: Heart,
      title: "Purposeful Work",
      description:
        "Create genuine impact in the lives of those we care for and their families each day.",
    },
    {
      icon: TrendingUp,
      title: "Professional Development",
      description:
        "Access continuous training, skill enhancement, and clear pathways for career progression.",
    },
    {
      icon: Users,
      title: "Collaborative Environment",
      description:
        "Join a dedicated team of professionals who value compassionate, person-centred care.",
    },
    {
      icon: Award,
      title: "Attractive Package",
      description:
        "Receive competitive remuneration, superannuation, professional development support, and leave entitlements.",
    },
  ];

  const openPositions = [
    {
      title: "Healthcare Assistant",
      department: "Care Services",
      type: "Full-Time",
      location: "Coolamon, NSW",
      description:
        "Become part of our care team as a Healthcare Assistant, supporting clients with daily activities and personal care. You will deliver compassionate assistance, help with meals and mobility, and foster a warm, respectful environment.",
      link: "/jobs/healthcare-assistant",
    },
    {
      title: "Physiotherapist",
      department: "Allied Health",
      type: "Full-Time / Part-Time",
      location: "Coolamon, NSW",
      description:
        "Join our allied health team to provide professional physiotherapy services to our clients. You will assess mobility needs, develop treatment plans, and work collaboratively to enhance quality of life and independence.",
      link: "/jobs/physiotherapist",
    },
    {
      title: "Driver",
      department: "Support Services",
      type: "Casual / Part-Time",
      location: "Coolamon, NSW",
      description:
        "Support our clients by providing safe, reliable transport to appointments, social activities, and community events. You will ensure comfortable journeys and contribute to maintaining independence and social connection.",
      link: "/jobs/driver",
    },
  ];

  const values = [
    "Compassionate and client-focused approach to care",
    "Excellent communication and interpersonal abilities",
    "Dedication to dignity, respect, and client wellbeing",
    "Capacity to work effectively within a team",
    "Relevant qualifications and experience in your discipline",
    "Enthusiasm for creating positive change",
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section
        className="py-12 px-6 md:px-12 lg:px-24"
        style={{ backgroundColor: "#F5F0F1" }}
      >
        <div className="max-w-7xl mx-auto">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-6">
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={index}>
                {index > 0 && <span>|</span>}
                {crumb.link ? (
                  <Link
                    to={crumb.link}
                    className="hover:text-[#E85D2C] transition-colors"
                  >
                    {crumb.label}
                  </Link>
                ) : (
                  <span className="text-gray-900">{crumb.label}</span>
                )}
              </React.Fragment>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="flex items-center gap-2 text-sm">
                <span className="text-[#E85D2C]">✦</span>
                <span className="text-gray-600">Join Our Team</span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Build a Fulfilling Career in Care
              </h1>

              <p className="text-lg text-gray-700 font-light leading-relaxed">
                At Allawah Lodge, we're seeking dedicated, caring professionals
                to join our team. Discover meaningful opportunities where you
                can truly make a difference.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="#positions"
                  className="inline-flex items-center gap-2 bg-[#E85D2C] hover:bg-[#d54d1f] text-white font-medium px-8 py-3.5 rounded-full transition-colors text-center"
                >
                  View Open Positions
                </a>
                <a
                  href="tel:+61269272706"
                  className="inline-flex items-center gap-2 bg-[#E85D2C] hover:bg-[#d54d1f] text-white font-medium px-6 py-3 rounded-full transition-colors"
                >
                  <Phone size={20} />
                  CALL: (02) 6927 2706
                </a>
              </div>
            </div>

            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden h-96 lg:h-[500px]">
                <img
                  src={careersHeroImage}
                  alt="Careers at Allawah Lodge"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="mx-4 md:mx-24 bg-white py-8 md:py-16 px-8 md:px-16 rounded-3xl mt-4 md:mt-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-12 space-y-4">
            <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
              <span className="text-[#E85D2C]">✦</span>
              <span>Why Choose Allawah</span>
            </div>

            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
              More Than Just Employment
            </h2>

            <p className="text-base text-gray-700 font-light leading-relaxed">
              Become part of a team that recognises your contributions and
              supports your professional journey.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-6 space-y-4">
                <div className="w-12 h-12 bg-[#FFF4F0] border-2 border-[#E85D2C] rounded-full flex items-center justify-center">
                  <benefit.icon size={24} className="text-[#E85D2C]" />
                </div>

                <h3 className="text-xl font-bold text-gray-900">
                  {benefit.title}
                </h3>

                <p className="text-sm text-gray-700 font-light leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions Section */}
      <section
        id="positions"
        className="mx-4 md:mx-24 bg-white py-8 md:py-16 px-8 md:px-16 rounded-3xl mt-4 md:mt-8"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-12 space-y-4">
            <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
              <span className="text-[#E85D2C]">✦</span>
              <span>Current Opportunities</span>
            </div>

            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
              Available Positions
            </h2>

            <p className="text-base text-gray-700 font-light leading-relaxed">
              Browse our current vacancies and discover the ideal role for your
              skills and aspirations.
            </p>
          </div>

          <div className="space-y-6">
            {openPositions.map((position, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-6 md:p-8">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                  <div className="flex-1 space-y-4">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">
                        {position.title}
                      </h3>

                      <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Briefcase size={16} />
                          <span>{position.department}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock size={16} />
                          <span>{position.type}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <MapPin size={16} />
                          <span>{position.location}</span>
                        </div>
                      </div>
                    </div>

                    <p className="text-sm text-gray-700 font-light leading-relaxed">
                      {position.description}
                    </p>
                  </div>

                  <Link
                    to={position.link}
                    className="inline-flex items-center gap-2 bg-[#E85D2C] hover:bg-[#d54d1f] text-white font-medium px-6 py-3 rounded-full transition-colors whitespace-nowrap"
                  >
                    Apply Now
                    <ArrowRight size={18} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="mx-4 md:mx-24 bg-white py-8 md:py-16 px-8 md:px-16 rounded-3xl mt-4 md:mt-8">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mx-auto space-y-6">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span className="text-[#E85D2C]">✦</span>
              <span>What We Value</span>
            </div>

            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
              The Attributes That Define Our Team
            </h2>

            <p className="text-base text-gray-700 font-light leading-relaxed">
              We seek individuals who embody our values and commitment to
              outstanding care. Here's what we look for in our team members:
            </p>

            <ul className="space-y-3">
              {values.map((value, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-[#FFF4F0] border-2 border-[#E85D2C] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-[#E85D2C] text-xs">✓</span>
                  </div>
                  <span className="text-sm text-gray-700 font-light leading-relaxed">
                    {value}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        id="apply"
        className="mx-4 md:mx-24 bg-[#E85D2C] py-8 md:py-16 px-8 md:px-16 rounded-3xl mt-4 md:mt-8 mb-4 md:mb-8"
      >
        <div className="max-w-7xl mx-auto">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">
              Ready to Begin Your Journey With Us?
            </h2>

            <p className="text-base md:text-lg text-white/90 font-light leading-relaxed">
              Join a team that champions compassion, professionalism, and making
              a genuine difference in people's lives.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="#positions"
                className="inline-flex items-center gap-2 bg-white hover:bg-gray-100 text-[#E85D2C] font-medium px-8 py-3.5 rounded-full transition-colors"
              >
                View Open Positions
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 border-2 border-white hover:bg-white hover:text-[#E85D2C] text-white font-medium px-8 py-3.5 rounded-full transition-colors"
              >
                Contact HR Team
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Careers;
